import React from "react";
import { FormControl, InputLabel, MenuItem, Select, Stack, Switch, FormControlLabel, TextField, Typography } from "@mui/material";
import { SEGMENT_FIELD_TYPES } from "../segmentRegistry";
import { getValueAtDescriptorPath } from "../elementEditing";
import { persistedCssColorError, registryFieldError } from "../inspector/inspectorModel";
import { VisualColorControl } from "../inspector/VisualSettingControls";

export default function VisualFieldElementEditor({ descriptor, form, disabled, onCommit }) {
  const field = descriptor?.field;
  const path = descriptor?.fieldPath || field?.path || "";
  if (!field || !path) return null;

  const value = getValueAtDescriptorPath(form, path, field.defaultValue ?? "");

  if (field.type === SEGMENT_FIELD_TYPES.COLOR) {
    const fallback = String(field.defaultValue || "#000000");
    return (
      <Stack spacing={1.2}>
        <Typography variant="caption" sx={{ color: "rgba(25,36,47,.62)" }}>
          Este control pertenece únicamente al elemento seleccionado.
        </Typography>
        <VisualColorControl
          label={field.label || "Color"}
          value={String(value ?? fallback)}
          fallback={fallback}
          disabled={disabled}
          helperText="Valor visual explícito de este elemento."
          allowExactCss
          validateExactValue={(candidate) => registryFieldError(field, candidate) || persistedCssColorError(candidate, field.maxLength || 100)}
          exactValueMaxLength={field.maxLength || 100}
          exactValueHelperText="Admite HEX, rgb/rgba, hsl/hsla y transparent."
          onChange={(next) => onCommit?.(path, next)}
          onReset={() => onCommit?.(path, fallback)}
        />
      </Stack>
    );
  }

  if (field.type === SEGMENT_FIELD_TYPES.NUMBER) {
    return (
      <TextField
        fullWidth
        size="small"
        type="number"
        label={field.label}
        value={value ?? field.defaultValue ?? ""}
        disabled={disabled}
        inputProps={{ min: field.limits?.min, max: field.limits?.max, step: field.limits?.step ?? 1 }}
        onChange={(event) => {
          const next = event.target.value === "" ? "" : Number(event.target.value);
          if (!registryFieldError(field, next)) onCommit?.(path, next);
        }}
      />
    );
  }

  if (field.type === SEGMENT_FIELD_TYPES.SELECT) {
    return (
      <FormControl fullWidth size="small" disabled={disabled}>
        <InputLabel>{field.label}</InputLabel>
        <Select label={field.label} value={value ?? ""} onChange={(event) => onCommit?.(path, event.target.value)}>
          {(field.options || []).map((option) => <MenuItem key={`${path}:${option.value}`} value={option.value}>{option.label}</MenuItem>)}
        </Select>
      </FormControl>
    );
  }

  if (field.type === SEGMENT_FIELD_TYPES.BOOLEAN) {
    return (
      <FormControlLabel
        control={<Switch checked={value !== false} disabled={disabled} onChange={(event) => onCommit?.(path, event.target.checked)} />}
        label={field.label}
      />
    );
  }

  return (
    <TextField
      fullWidth
      size="small"
      multiline={field.type === SEGMENT_FIELD_TYPES.TEXTAREA}
      minRows={field.type === SEGMENT_FIELD_TYPES.TEXTAREA ? 3 : undefined}
      label={field.label}
      value={value ?? ""}
      disabled={disabled}
      inputProps={{ maxLength: field.maxLength }}
      onChange={(event) => onCommit?.(path, event.target.value)}
    />
  );
}
